import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ReactDOM from "react-dom";
import PropTypes from "prop-types";
import styled, { keyframes, ThemeProvider } from "styled-components";
import FocusTrap from "focus-trap-react";

import { css } from "@styled-system/css";
import { themeGet } from "@styled-system/theme-get";

import Icon from "../Icon";
import Button from "../Button";
import Flex from "../Flex";
import Box from "../Box";

const scaleIn = keyframes`
  0% {
    transform: scale(0);
  }
  100% {
    transform: scale(1);
  }
`;

const fadeIn = keyframes`
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
`;

const Overlay = styled(Flex)`
  position: fixed;
  background: rgba(0, 0, 0, 0.7);
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  margin: 0;
  opacity: 0;
  transition: all 300ms ease-in-out;
  opacity: 1;
  z-index: 900;
  visibility: visible;
  animation: 300ms ${fadeIn} ease-in-out;
  justify-content: center;
  align-items: center;
`;
const Container = styled(Box)`
  position: relative;
  z-index: 1000;
  animation:
    300ms ${fadeIn} ease-in-out,
    300ms ${scaleIn} ease-in-out;
  display: flex;
  flex-direction: column;
  resize: both;
  overflow: ${(props) => (props.overflow === "visible" ? "visible" : "hidden")};
`;

const CloseButton = styled(Button)((props) =>
  css({
    position: "absolute",
    top: "s",
    right: "s",
    bg: "transparent",
    width: "20px",
    height: "20px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "10px",
    color: themeGet("colors.subText")(props),
    borderColor: "transparent",
    "&:hover": {
      bg: themeGet("colors.hoverSurface")(props),
      borderColor: themeGet("colors.surfaceRaisedBorder")(props),
    },
    "&:focus": {
      outline: "0",
      boxShadow:
        themeGet("shadows.thinOutline")(props) +
        " " +
        themeGet("colors.black20")(props),
    },
  })
);

const HeaderContent = styled.div`
  display: flex;
  align-items: flex-start;
  flex-direction: row-reverse;
  justify-content: space-between;
  width: 100%;
  padding-bottom: ${(props) => themeGet("space.r")(props)};
  border-bottom: solid 1px ${(props) => themeGet("colors.surfaceBorder")(props)};
  flex: 0 0 auto;
`;

const FooterContent = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  width: 100%;
  padding-top: ${(props) => themeGet("space.r")(props)};
  border-top: solid 1px ${(props) => themeGet("colors.surfaceBorder")(props)};
  flex: 0 0 auto;
`;

const ScrollableContent = styled.div`
  --scrollbar-size: 8px;
  --scrollbar-minlength: 30px; /* Minimum length of scrollbar thumb (width of horizontal, height of vertical) */
  --scrollbar-ff-width: thin; /* FF-only accepts auto, thin, none */
  --scrollbar-track-color: rgba(0, 0, 0, 0.05);
  --scrollbar-color: rgba(0, 0, 0, 0.2);
  --scrollbar-color-hover: rgba(0, 0, 0, 0.35);
  --scrollbar-color-active: rgba(0, 0, 0, 0.5);
  height: 100%;
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: ${(props) => (props.overflow === "visible" ? "visible" : "auto")};
  margin-top: ${(props) => (props.headerContent ? "0" : "20px")};
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: var(--scrollbar-ff-width);
  scrollbar-color: var(--scrollbar-color) var(--scrollbar-track-color);
  &::-webkit-scrollbar {
    height: var(--scrollbar-size);
    width: var(--scrollbar-size);
  }
  &::-webkit-scrollbar-track {
    background-color: var(--scrollbar-track-color);
  }
  &::-webkit-scrollbar-thumb {
    background-color: var(--scrollbar-color);
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background-color: var(--scrollbar-color-hover);
  }
  &::-webkit-scrollbar-thumb:active {
    background-color: var(--scrollbar-color-active);
  }
  &::-webkit-scrollbar-thumb:vertical {
    min-height: var(--scrollbar-minlength);
  }
  &::-webkit-scrollbar-thumb:horizontal {
    min-width: var(--scrollbar-minlength);
  }
`;

const isHidden = (el) => window.getComputedStyle(el).display === "none";

// react-dates appendToBody / withPortal render outside the dialog. Let those
// clicks through so the focus trap does not swallow day selection.
const isPortaledPickerClick = (event) =>
  Boolean(
    event.target.closest?.(".SingleDatePicker_picker, .DateRangePicker_picker")
  );

const Modal = ({
  children,
  width = "350px",
  height = "auto",
  maxWidth = "90vw",
  maxHeight = "90vh",
  minWidth,
  minHeight,
  overflow,
  onClose,
  theme,
  visible,
  overlayID = "modal-overlay",
  modalID = "modal-container",
  headerContent,
  footerContent,
  allowOutsideClick = isPortaledPickerClick,
  ...restProps
}) => {
  const lastActiveElementRef = useRef(null);
  const [isSelectingText, setIsSelectingText] = useState(false);
  const isSelectingTextRef = useRef(false);
  const closeButtonRef = useRef(null);

  const ariaLabel = useMemo(() => {
    if (restProps.ariaLabel) {
      return restProps.ariaLabel;
    } else if (typeof headerContent === "string") {
      return headerContent;
    }
  }, [restProps.ariaLabel, headerContent]);

  const focusLastActiveElement = useCallback(() => {
    const lastActiveElement = lastActiveElementRef.current;
    if (!lastActiveElement) return;

    if (
      lastActiveElement?.dataset.actionMenuId &&
      isHidden(lastActiveElement?.parentNode)
    ) {
      const actionMenu = document.getElementById(
        lastActiveElement.dataset.actionMenuId
      );

      actionMenu.focus();
    } else {
      lastActiveElement.focus();
    }
  }, []);

  // Restore focus to the opener when the modal closes.
  useEffect(() => {
    if (visible) {
      if (!lastActiveElementRef.current) {
        lastActiveElementRef.current = document.activeElement;
      }
      return undefined;
    }
    focusLastActiveElement();
    lastActiveElementRef.current = null;
    return undefined;
  }, [visible, focusLastActiveElement]);

  useEffect(() => {
    isSelectingTextRef.current = isSelectingText;
  }, [isSelectingText]);

  useEffect(() => {
    if (!visible) return;

    /* Adding onClick handler on the Overlay does not work.
     * So we add a global listener and check if the element clicked is the
     * overlay via overlayID.
     */
    const handleMouseDown = (e) => {
      // Check if the user is starting to select text
      if (e.target.closest(`#${modalID}`)) {
        setIsSelectingText(true);
      }
    };

    const handleMouseUp = () => {
      // Reset text selection state after a short delay
      setTimeout(() => {
        setIsSelectingText(false);
      }, 100);
    };

    const handleClick = (e) => {
      if (e.target.id === overlayID && !isSelectingTextRef.current) {
        onClose();
      }
    };

    const handleTouchStart = (e) => {
      if (e.target.id === overlayID) {
        onClose();
      }
    };

    // Add event listeners
    window.addEventListener("mousedown", handleMouseDown, true);
    window.addEventListener("mouseup", handleMouseUp, true);
    window.addEventListener("click", handleClick, true);
    window.addEventListener("touchstart", handleTouchStart, true);

    return () => {
      window.removeEventListener("mousedown", handleMouseDown, true);
      window.removeEventListener("mouseup", handleMouseUp, true);
      window.removeEventListener("click", handleClick, true);
      window.removeEventListener("touchstart", handleTouchStart, true);
    };
  }, [visible, onClose, overlayID, modalID]);

  if (!visible) return null;

  const component = (
    <Overlay
      alignItems="center"
      justifyContent="center"
      id={overlayID}
      {...restProps}
    >
      {visible && (
        <FocusTrap
          focusTrapOptions={{
            onDeactivate: onClose,
            allowOutsideClick,
            // Use initialFocus to point to close button, which is always present
            // This ensures FocusTrap has a valid focus target from the start, preventing errors
            // in test environments where content might not be fully rendered
            initialFocus: () =>
              closeButtonRef.current || document.querySelector(".modal-close"),
            // Use fallbackFocus as backup in case initialFocus fails
            fallbackFocus: () =>
              closeButtonRef.current || document.querySelector(".modal-close"),
          }}
        >
          <div
            id="modal"
            role="dialog"
            aria-modal="true"
            aria-label={ariaLabel}
          >
            <Container
              width={width}
              height={height}
              maxWidth={maxWidth}
              maxHeight={maxHeight}
              minWidth={minWidth}
              minHeight={minHeight}
              overflow={overflow}
              borderRadius="2"
              bg="surfaceRaised"
              p="r"
              id={modalID}
            >
              {/* Hidden but focusable element to ensure FocusTrap always has a tabbable node */}
              <input
                type="text"
                tabIndex={0}
                aria-hidden="true"
                style={{
                  position: "absolute",
                  left: "-9999px",
                  width: "1px",
                  height: "1px",
                  opacity: 0,
                  pointerEvents: "none",
                }}
                readOnly
              />
              {headerContent ? (
                <HeaderContent>
                  <CloseButton
                    ref={closeButtonRef}
                    onClick={onClose}
                    className="modal-close"
                    p="0"
                    aria-label="Close dialog"
                  >
                    <Icon icon={["fas", "xmark"]} color="subText" size="lg" />
                  </CloseButton>
                  <Box mr="xl" width="100%">
                    {headerContent}
                  </Box>
                </HeaderContent>
              ) : (
                <CloseButton
                  ref={closeButtonRef}
                  onClick={onClose}
                  className="modal-close"
                  p="0"
                  aria-label="Close dialog"
                >
                  <Icon icon={["fas", "xmark"]} color="subText" size="lg" />
                </CloseButton>
              )}
              <ScrollableContent
                headerContent={headerContent}
                overflow={overflow}
              >
                {children}
              </ScrollableContent>
              {footerContent && <FooterContent>{footerContent}</FooterContent>}
            </Container>
          </div>
        </FocusTrap>
      )}
    </Overlay>
  );

  return ReactDOM.createPortal(
    theme ? (
      <ThemeProvider theme={theme}>{component}</ThemeProvider>
    ) : (
      component
    ),
    document.body
  );
};

Modal.propTypes = {
  /** Specifies the children of the Modal */
  children: PropTypes.oneOfType([
    PropTypes.element,
    PropTypes.string,
    PropTypes.node,
  ]),
  /** Specifies content for the header of the modal */
  headerContent: PropTypes.oneOfType([PropTypes.element, PropTypes.node]),
  /** Specifies content for the header of the modal */
  footerContent: PropTypes.oneOfType([PropTypes.element, PropTypes.node]),
  /** Specifies the width of the Modal */
  width: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]),
  /** Specifies the max width of the Modal */
  maxWidth: PropTypes.string,
  /** Specifies the min width of the Modal */
  minWidth: PropTypes.string,
  /** Specifies the height of the Modal */
  height: PropTypes.string,
  /** Specifies the max height of the Modal */
  maxHeight: PropTypes.string,
  /** Specifies the min height of the Modal */
  minHeight: PropTypes.string,
  /** Specifies the visibility of the Modal */
  visible: PropTypes.bool,
  /** Specifies the function to run on clicking X icon. Ensure that this function will close Modal through the `visible` prop */
  onClose: PropTypes.func,
  /** Specifies whether the Modal overflow is visible or not, default is `hidden`. If height is not enough, vertical scrollbar will be displayed (`overflow-y: auto`) */
  overflow: PropTypes.string,
  /** Specifies the id of the overlay element for targeting */
  overlayID: PropTypes.string,
  /** Specifies the id of the modal element for targeting */
  modalID: PropTypes.string,
  /** Sets the theme for the Modal */
  theme: PropTypes.object,
  /** Specifies the aria-label for the modal. Set this if headerContent is not defined as a string. */
  ariaLabel: PropTypes.string,
  /** Lets clicks land on content portaled outside the dialog (date pickers). */
  allowOutsideClick: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
};

export default Modal;
